import styles from './CTACentered.module.scss';
import { Colors } from '../../../../theme';
import type { AnchorTarget } from '../../../../ts-types';
import { SubscriptionFormWithDisclaimer } from '../../SubscriptionForm';
import {
  LegacyButton,
  LegacyButtonProps,
} from '../../../navigation/LegacyButton';
import { DarkBackgroundButton } from '../../../navigation/DarkBackgroundButton';
import { CTATextContent } from '../CTATextContent';
import { BgColor, CTABtnColor, DeprecatedBgColor } from '../types';
import { getBgColor, getCtaBtnColor } from '../utils';
import { LegacyColorMap } from '@/theme/LegacyColorMap';

export type Props = {
  /**
   * Sets the container's background color, must be one of type `lava60` `ocean40` `sun60` `white`
   * `zapier` `night` `moss` `neutral800` `neutral100` `earth`
   */
  backgroundColor?: BgColor | DeprecatedBgColor;
  /**
   * max-width in pixels applied to the content area of container.
   */
  maxWidth?: number;
  /**
   * Heading text content.
   */
  heading: React.ReactNode;
  /**
   * Text content displayed below the heading.
   */
  subHeading?: React.ReactNode;
  /**
   * Text content displayed below the sub-heading.
   */
  description?: React.ReactNode;
  /**
   * Text content of the CTA button.
   */
  ctaText?: React.ReactNode;
  /**
   * href for the CTA button.
   */
  ctaHref?: string;
  /**
   * Color of the CTA button.
   *
   * Must be one of the type `'primary' | 'primary-alt' | 'primary-dark' | 'brand'`
   */
  ctaBtnColor?: CTABtnColor;
  /**
   * CTA button click handler.
   */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * CTA button keyDown handler.
   */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * Whether the subscription form is enabled in place of CTA button.
   */
  isSubscriptionForm?: boolean;
  /**
   * Placeholder text to display inside the subscription form input field.
   */
  subscriptionPlaceholder?: string;
  /**
   * Function that gets called on subscription form submit.
   */
  onSubscriptionFormSubmit?: (subscriptionValue?: string) => void;
  /**
   * The `target` attribute for the CTA button.
   */
  ctaTarget?: AnchorTarget;
};

const getSubscriptionFormButton = (
  bgColor: BgColor | DeprecatedBgColor,
  ctaBtnColor?: CTABtnColor
) => {
  const btnColor = getCtaBtnColor(bgColor, ctaBtnColor);
  const btnProps = {
    children: 'Submit',
    size: 'medium' as const,
    type: 'submit' as LegacyButtonProps['type'],
  };
  if (btnColor === 'primary-dark')
    return <DarkBackgroundButton {...btnProps} />;
  else {
    return (
      <LegacyButton
        {...btnProps}
        // "primary-alt" no longer exists as of V8 and is now mapped to "primary"
        color={btnColor === 'primary-alt' ? 'primary' : btnColor}
        size="small"
      />
    );
  }
};

/**
 * `CTACentered` is used to direct our customers to take a specific action. It features a heading, sub-heading, description, and subscription form with a description
 */
export const CTACentered = ({
  backgroundColor = 'neutral100',
  ctaHref,
  ctaBtnColor,
  ctaText,
  description,
  heading,
  maxWidth = 570,
  onCtaClick,
  onCtaKeyDown,
  subHeading,
  isSubscriptionForm,
  subscriptionPlaceholder,
  onSubscriptionFormSubmit,
  ctaTarget,
}: Props) => {
  const bgColor = getBgColor(backgroundColor);

  const isZapierOrange = bgColor === 'zapier';

  if (isZapierOrange && isSubscriptionForm) {
    console.error(
      'Do not use the subscription form with the zapier background. The disclaimer text accessibility ' +
        'is poor on the zapier background.'
    );
  }

  return (
    <div
      className={styles.root}
      style={{
        '--_zds-cta-centered-bg-color': Colors[LegacyColorMap[bgColor]],
      }}
      data-zds
    >
      <div
        className={styles.mediaContent}
        style={{
          '--_zds-cta-centered-max-width': `${maxWidth}px`,
        }}
        data-zds
      >
        <CTATextContent
          heading={heading}
          subHeading={subHeading}
          description={description}
          ctaText={!isSubscriptionForm ? ctaText : undefined}
          ctaHref={!isSubscriptionForm ? ctaHref : undefined}
          ctaBtnColor={ctaBtnColor}
          onCtaClick={!isSubscriptionForm ? onCtaClick : undefined}
          onCtaKeyDown={onCtaKeyDown}
          bgColor={bgColor}
          alignment="center"
          ctaTarget={ctaTarget}
        />
      </div>
      {isSubscriptionForm ? (
        <SubscriptionFormWithDisclaimer
          placeholderText={subscriptionPlaceholder || ''}
          renderButton={() =>
            getSubscriptionFormButton(backgroundColor, ctaBtnColor)
          }
          onSubmit={onSubscriptionFormSubmit}
          disclaimerTextColor={
            bgColor === 'neutral100' ? 'primary' : 'secondary'
          }
          disclaimerTextType="Body1"
        />
      ) : null}
    </div>
  );
};
